import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Coffee, Plus, Minus, Trash2, Search, ShoppingCart, DollarSign, X, Check, Printer, Loader2, CreditCard, Smartphone, AlertCircle, Tag, Percent, ShoppingBag, ArrowUp } from 'lucide-react';
import cafeService from '../services/cafe';
import printProfileService from '../services/printProfile';
import { printReceipt } from '../utils/printReceipt';
import useIsMobile from '../hooks/useIsMobile';

const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);
const METODE = ['TUNAI', 'QRIS', 'TRANSFER', 'KARTU'];

const CafePOS = ({ onNavigate }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategori, setFilterKategori] = useState('All');
  const [cart, setCart] = useState([]);
  const [bayar, setBayar] = useState('');
  const [metode, setMetode] = useState('TUNAI');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [printProfile, setPrintProfile] = useState({ header1: '', header2: '', header3: '', footer1: '', footer2: '', footer3: '' });

  // Total discount state
  const [diskonTotal, setDiskonTotal] = useState('');
  const [diskonTotalTipe, setDiskonTotalTipe] = useState('PERSEN');
  const [showDiskonTotal, setShowDiskonTotal] = useState(false);
  const [bayarPasFlash, setBayarPasFlash] = useState(false);

  // Mobile state
  const isMobile = useIsMobile();
  const [showMobileCart, setShowMobileCart] = useState(false);

  const KATEGORI = ['MAKANAN', 'MINUMAN', 'SNACK', 'LAINNYA'];

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { isActive: 'true' };
      if (searchTerm) params.search = searchTerm;
      if (filterKategori !== 'All') params.kategori = filterKategori;
      const data = await cafeService.getProducts(params);
      setProducts(data.products || []);
    } catch (err) {
      setError(err?.message || 'Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterKategori]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Fetch print profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await printProfileService.getProfile();
        setPrintProfile({
          header1: data.header1 || '',
          header2: data.header2 || '',
          header3: data.header3 || '',
          footer1: data.footer1 || '',
          footer2: data.footer2 || '',
          footer3: data.footer3 || ''
        });
      } catch (err) {
        // silent fail - print profile is optional
      }
    };
    fetchProfile();
  }, []);

  const addToCart = (product) => {
    setCart(prev => {
      const exists = prev.find(c => c.productId === product._id);
      if (exists) {
        return prev.map(c => c.productId === product._id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { 
        productId: product._id, 
        nama: product.nama, 
        harga: product.harga, 
        quantity: 1,
        diskon: 0,
        diskonTipe: ''
      }];
    });
  };

  const updateQty = (productId, delta) => {
    setCart(prev => {
      const item = prev.find(c => c.productId === productId);
      if (!item) return prev;
      const newQty = item.quantity + delta;
      if (newQty <= 0) return prev.filter(c => c.productId !== productId);
      return prev.map(c => c.productId === productId ? { ...c, quantity: newQty } : c);
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(c => c.productId !== productId));
  };

  // Per-item discount handlers
  const setItemDiskon = (productId, value, tipe) => {
    setCart(prev => prev.map(c => 
      c.productId === productId ? { ...c, diskon: value, diskonTipe: tipe } : c
    ));
  };

  const toggleItemDiskon = (productId) => {
    setCart(prev => prev.map(c => 
      c.productId === productId 
        ? { ...c, diskonTipe: c.diskonTipe ? '' : 'PERSEN', diskon: c.diskonTipe ? 0 : c.diskon || 0 }
        : c
    ));
  };

  // Calculate per-item discount Rp
  const getItemDiskonRp = (item) => {
    const subtotal = item.harga * item.quantity;
    if (!item.diskonTipe || !item.diskon) return 0;
    if (item.diskonTipe === 'PERSEN') return Math.round(subtotal * Math.min(item.diskon, 100) / 100);
    if (item.diskonTipe === 'NOMINAL') return Math.min(item.diskon, subtotal);
    return 0;
  };

  const getItemSubtotalDiskon = (item) => {
    return (item.harga * item.quantity) - getItemDiskonRp(item);
  };

  // Calculate total discount Rp
  const getTotalDiskonRp = () => {
    const totalSetelahItem = cart.reduce((sum, c) => sum + getItemSubtotalDiskon(c), 0);
    const val = Number(diskonTotal) || 0;
    if (!showDiskonTotal || !val) return 0;
    if (diskonTotalTipe === 'PERSEN') return Math.round(totalSetelahItem * Math.min(val, 100) / 100);
    if (diskonTotalTipe === 'NOMINAL') return Math.min(val, totalSetelahItem);
    return 0;
  };

  const totalBelanja = cart.reduce((sum, c) => sum + getItemSubtotalDiskon(c), 0) - getTotalDiskonRp();
  const totalSebelumDiskon = cart.reduce((sum, c) => sum + (c.harga * c.quantity), 0);
  const totalDiskonItem = cart.reduce((sum, c) => sum + getItemDiskonRp(c), 0);
  const totalDiskonAll = totalDiskonItem + getTotalDiskonRp();
  const jumlahBayar = Number(bayar) || 0;
  const kembalian = Math.max(0, jumlahBayar - totalBelanja);

  const handleCheckout = async () => {
    if (cart.length === 0) {
      if (isMobile) setShowMobileCart(false);
      return;
    }
    if (jumlahBayar < totalBelanja) {
      setError('Uang pembayaran kurang');
      if (isMobile) setShowMobileCart(false);
      return;
    }
    setCheckoutLoading(true);
    setError(null);
    try {
      const itemsPayload = cart.map(c => ({
        productId: c.productId,
        quantity: c.quantity,
        diskon: Number(c.diskon) || 0,
        diskonTipe: c.diskonTipe || ''
      }));

      const payload = {
        items: itemsPayload,
        bayar: jumlahBayar,
        metodePembayaran: metode
      };

      if (showDiskonTotal && Number(diskonTotal) > 0) {
        payload.diskonTotal = Number(diskonTotal);
        payload.diskonTotalTipe = diskonTotalTipe;
      }

      const result = await cafeService.createSale(payload);
      setSuccess(result);
      setCart([]);
      setBayar('');
      setMetode('TUNAI');
      setDiskonTotal('');
      setDiskonTotalTipe('PERSEN');
      setShowDiskonTotal(false);
      setShowMobileCart(false);
    } catch (err) {
      setError(err?.message || 'Terjadi kesalahan');
      setShowMobileCart(false); // Close bottom sheet so user can see error
    } finally {
      setCheckoutLoading(false);
    }
  };

  const resetSuccess = () => {
    setSuccess(null);
  };

  // Print receipt function (uses shared utility)
  const handlePrint = (sale, size) => {
    printReceipt(sale, printProfile, size);
  };

  // Success receipt view
  if (success) {
    return (
      <div className="animate-fade-in">
        <div className="max-w-md mx-auto card p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Transaksi Berhasil!</h2>
          <p className="text-sm text-gray-500 mt-1">{success.nomorNota}</p>

          <div className="bg-gray-50 rounded-xl p-4 mt-4 text-left space-y-2">
            {success.items?.map((item, idx) => {
              const hasDiskon = item.diskonTipe && item.diskon > 0;
              return (
                <div key={idx}>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{item.product?.nama} x{item.quantity}</span>
                    <span className="font-semibold">{formatRupiah(item.subtotalDiskon || item.subtotal)}</span>
                  </div>
                  {hasDiskon && (
                    <div className="text-right text-[10px] text-red-500">
                      Diskon {item.diskonTipe === 'PERSEN' ? `${item.diskon}%` : formatRupiah(item.diskon)} 
                      (hemat {formatRupiah(item.subtotal - (item.subtotalDiskon || item.subtotal))})
                    </div>
                  )}
                </div>
              );
            })}
            {(success.diskonTotalRp > 0 || success.items?.some(i => i.diskonTipe)) && (
              <>
                <hr className="border-gray-200" />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Subtotal</span>
                  <span>{formatRupiah(success.totalSebelumDiskon || success.totalHarga)}</span>
                </div>
                {success.items?.some(i => i.diskonTipe) && (
                  <div className="flex justify-between text-xs text-red-500">
                    <span>Diskon Item</span>
                    <span>-{formatRupiah(success.totalSebelumDiskon - success.totalHarga - (success.diskonTotalRp || 0))}</span>
                  </div>
                )}
                {success.diskonTotalRp > 0 && (
                  <div className="flex justify-between text-xs text-red-500">
                    <span>Diskon Total{success.diskonTotalTipe === 'PERSEN' ? ` (${success.diskonTotal}%)` : ''}</span>
                    <span>-{formatRupiah(success.diskonTotalRp)}</span>
                  </div>
                )}
              </>
            )}
            <hr className="border-gray-200" />
            <div className="flex justify-between text-sm font-bold">
              <span>Total</span>
              <span>{formatRupiah(success.totalHarga)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-500">
              <span>Bayar</span>
              <span>{formatRupiah(success.bayar)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-500">
              <span>Kembalian</span>
              <span className="text-green-600 font-semibold">{formatRupiah(success.kembalian)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>{success.metodePembayaran}</span>
              <span>{format(new Date(success.createdAt), 'HH:mm')}</span>
            </div>
          </div>

          {/* Print options */}
          <div className="mt-4 space-y-2">
            <p className="text-xs text-gray-500 font-medium">Cetak Struk</p>
            <div className="flex space-x-2 justify-center">
              <button onClick={() => handlePrint(success, '58')}
                className="flex-1 px-3 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg border border-blue-200 transition-all active:scale-95 flex items-center justify-center space-x-1.5">
                <Printer className="w-4 h-4" /><span>Thermal 58mm</span>
              </button>
              <button onClick={() => handlePrint(success, '80')}
                className="flex-1 px-3 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg border border-blue-200 transition-all active:scale-95 flex items-center justify-center space-x-1.5">
                <Printer className="w-4 h-4" /><span>Thermal 80mm</span>
              </button>
            </div>
          </div>

          <div className="flex space-x-3 mt-4 justify-center">
            <button onClick={resetSuccess} className="btn-primary">
              <Plus className="w-4 h-4" /><span>Transaksi Baru</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Title — hidden on mobile (already in mobile header) */}
      {!isMobile && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">POS Kasir</h2>
            <p className="text-sm text-gray-500">Pilih produk untuk transaksi penjualan</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-500" /><p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Product Grid */}
        <div className="lg:col-span-2 space-y-3">
          <div className="card p-3">
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" placeholder="Cari produk..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-gray-50" />
              </div>
              <select value={filterKategori} onChange={e => setFilterKategori(e.target.value)} className="select-field text-sm">
                <option value="All">Semua</option>
                {KATEGORI.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /><span className="mt-3 text-gray-500">Loading...</span></div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {products.map((p, idx) => {
                const inCart = cart.find(c => c.productId === p._id);
                return (
                  <button key={p._id} onClick={() => addToCart(p)}
                    className={`card p-3 text-left hover:shadow-md transition-all animate-slide-up ${inCart ? 'ring-2 ring-amber-400 bg-amber-50' : ''}`}
                    style={{ animationDelay: `${idx * 30}ms` }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-mono text-gray-400">{p.kode}</span>
                      {inCart && <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">{inCart.quantity}x</span>}
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{p.nama}</p>
                    <p className="text-xs font-bold text-amber-600 mt-1">{formatRupiah(p.harga)}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-[10px] flex items-center space-x-1 ${p.stok === 0 ? 'text-red-500' : p.stok < 5 ? 'text-amber-600' : 'text-green-600'}`}>
                        {p.stok === 0 ? <><AlertCircle className="w-3 h-3" /><span>Habis</span></>
                          : p.stok < 5 ? <><AlertCircle className="w-3 h-3" /><span>Stok: {p.stok}</span></>
                          : <span>Stok: {p.stok}</span>}
                      </span>
                      <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{p.kategori}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <Coffee className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Tidak ada produk</p>
            </div>
          )}
        </div>

        {/* Cart — DESKTOP */}
        {!isMobile && (
          <div className="lg:col-span-1">
            <div className="card flex flex-col h-full">
              <div className="card-header flex items-center space-x-2">
                <ShoppingCart className="w-5 h-5 text-amber-600" />
                <h3 className="section-title">Keranjang</h3>
                {cart.length > 0 && <span className="text-xs font-bold text-white bg-amber-500 px-2 py-0.5 rounded-full">{cart.length}</span>}
              </div>
              <div className="card-body flex-1 flex flex-col">
                <div className="flex-1 space-y-2 min-h-[200px] max-h-[320px] overflow-y-auto">
                  {cart.length > 0 ? cart.map((item, idx) => {
                    const diskonRp = getItemDiskonRp(item);
                    const subtotalDiskon = getItemSubtotalDiskon(item);
                    return (
                      <div key={item.productId} className="bg-gray-50 rounded-lg p-2.5 animate-slide-up" style={{ animationDelay: `${idx * 30}ms` }}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-900 truncate">{item.nama}</p>
                            <p className="text-[10px] text-gray-400">{formatRupiah(item.harga)} x {item.quantity}</p>
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            <button onClick={() => updateQty(item.productId, -1)} className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-600 text-xs font-bold"><Minus className="w-3 h-3" /></button>
                            <span className="w-6 text-center text-xs font-bold">{item.quantity}</span>
                            <button onClick={() => updateQty(item.productId, 1)} className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-600 text-xs font-bold"><Plus className="w-3 h-3" /></button>
                            <button onClick={() => toggleItemDiskon(item.productId)} 
                              className={`w-6 h-6 flex items-center justify-center rounded ${item.diskonTipe ? 'bg-red-100 text-red-500' : 'bg-gray-200 text-gray-400'} hover:bg-red-200`} 
                              title={item.diskonTipe ? 'Edit diskon' : 'Tambah diskon'}>
                              <Tag className="w-3 h-3" />
                            </button>
                            <button onClick={() => removeFromCart(item.productId)} className="w-6 h-6 flex items-center justify-center rounded text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                        {item.diskonTipe && (
                          <div className="flex items-center space-x-1.5 mt-1.5 ml-1">
                            <Percent className="w-3 h-3 text-red-400" />
                            <select value={item.diskonTipe} onChange={e => setItemDiskon(item.productId, item.diskon, e.target.value)}
                              className="text-[10px] border border-gray-200 rounded px-1 py-0.5 bg-white">
                              <option value="PERSEN">%</option>
                              <option value="NOMINAL">Rp</option>
                            </select>
                            <input type="number" value={item.diskon || ''} 
                              onChange={e => setItemDiskon(item.productId, Number(e.target.value) || 0, item.diskonTipe)}
                              placeholder="0" min="0" max={item.diskonTipe === 'PERSEN' ? 100 : item.harga * item.quantity}
                              className="w-16 text-[10px] border border-gray-200 rounded px-1.5 py-0.5 bg-white text-right"
                              onClick={e => e.stopPropagation()} />
                            {diskonRp > 0 && (
                              <span className="text-[10px] text-red-500 font-medium">-{formatRupiah(diskonRp)}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }) : (
                    <div className="text-center py-12">
                      <ShoppingCart className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-xs text-gray-400">Belum ada item</p>
                    </div>
                  )}
                </div>

                <hr className="border-gray-100 my-2" />

                <div className="space-y-1.5">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-400">Subtotal</span>
                    <span className="text-gray-600">{formatRupiah(totalSebelumDiskon)}</span>
                  </div>

                  {totalDiskonItem > 0 && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-red-400">Diskon Item</span>
                      <span className="text-red-500 font-medium">-{formatRupiah(totalDiskonItem)}</span>
                    </div>
                  )}

                  {/* Total discount toggle & input */}
                  <div className="border border-dashed border-gray-200 rounded-lg p-2">
                    <button onClick={() => setShowDiskonTotal(!showDiskonTotal)} 
                      className="flex items-center space-x-1.5 text-xs text-gray-500 hover:text-red-500 transition-colors">
                      <Tag className={`w-3.5 h-3.5 ${showDiskonTotal ? 'text-red-500' : ''}`} />
                      <span>{showDiskonTotal ? 'Tutup' : 'Diskon Total'}</span>
                      {showDiskonTotal && getTotalDiskonRp() > 0 && (
                        <span className="text-red-500 font-semibold">-{formatRupiah(getTotalDiskonRp())}</span>
                      )}
                    </button>
                    {showDiskonTotal && (
                      <div className="flex items-center space-x-1.5 mt-1.5">
                        <select value={diskonTotalTipe} onChange={e => setDiskonTotalTipe(e.target.value)}
                          className="text-[10px] border border-gray-200 rounded px-1 py-0.5 bg-white">
                          <option value="PERSEN">%</option>
                          <option value="NOMINAL">Rp</option>
                        </select>
                        <input type="number" value={diskonTotal} 
                          onChange={e => setDiskonTotal(e.target.value)}
                          placeholder="0" min="0" max={diskonTotalTipe === 'PERSEN' ? 100 : totalBelanja}
                          className="flex-1 text-[10px] border border-gray-200 rounded px-1.5 py-0.5 bg-white text-right" />
                        {getTotalDiskonRp() > 0 && (
                          <span className="text-[10px] text-red-500 font-medium">hemat {formatRupiah(getTotalDiskonRp())}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Total</span>
                    <span className="text-lg font-bold text-gray-900">{formatRupiah(totalBelanja)}</span>
                  </div>

                  {totalDiskonAll > 0 && (
                    <div className="text-[10px] text-green-600 font-medium text-right">
                      Hemat {formatRupiah(totalDiskonAll)}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-2">
                    <select value={metode} onChange={e => setMetode(e.target.value)} className="select-field text-xs">
                      {METODE.map(m => <option key={m} value={m}>{m === 'QRIS' ? '💳 QRIS' : m}</option>)}
                    </select>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[10px] text-gray-400">Rp</span>
                      <input type="number" value={bayar} onChange={e => setBayar(e.target.value)}
                        className="input-field w-full text-xs pl-7" placeholder="Bayar" min="0" />
                    </div>
                    <button onClick={() => { setBayar(String(totalBelanja)); setBayarPasFlash(true); setTimeout(() => setBayarPasFlash(false), 600); }}
                      disabled={cart.length === 0}
                      className={`px-2 py-2.5 text-xs font-semibold rounded-lg border transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${bayarPasFlash ? 'bg-green-200 border-green-400 text-green-800 scale-105' : 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200'}`}
                      title="Isi nominal bayar sesuai total">
                      Bayar Pas
                    </button>
                  </div>

                  {jumlahBayar >= totalBelanja && totalBelanja > 0 && (
                    <div className="text-right text-xs text-green-600 font-semibold">
                      Kembali: {formatRupiah(kembalian)}
                    </div>
                  )}

                  <button onClick={handleCheckout} disabled={cart.length === 0 || checkoutLoading || jumlahBayar < totalBelanja}
                    className="btn-primary w-full text-sm justify-center">
                    {checkoutLoading ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Memproses...</span></>
                      : <><DollarSign className="w-4 h-4" /><span>Bayar {formatRupiah(totalBelanja)}</span></>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── MOBILE: Floating Cart Bar ───────────────── */}
      {isMobile && cart.length > 0 && (
        <>
          <div className="fixed bottom-[60px] left-0 right-0 z-30 px-3 pb-1 safe-area-bottom">
            <button
              onClick={() => setShowMobileCart(true)}
              className="w-full flex items-center justify-between bg-gradient-to-r from-amber-600 to-amber-500 rounded-xl px-4 py-3 shadow-xl shadow-amber-200 active:scale-[0.98] transition-all"
            >
              <div className="flex items-center space-x-2.5">
                <div className="relative">
                  <ShoppingBag className="w-6 h-6 text-white" />
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center text-[9px] font-bold text-amber-600 bg-white rounded-full">
                    {cart.length}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-xs text-white/80">{cart.length} item{cart.length > 1 ? 's' : ''}</p>
                  <p className="text-sm font-bold text-white">{formatRupiah(totalBelanja)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="text-xs font-semibold text-white">Lihat</span>
                <ArrowUp className="w-4 h-4 text-white" />
              </div>
            </button>
          </div>

          {/* ─── MOBILE: Cart Bottom Sheet ────────────── */}
          {showMobileCart && (
            <div className="fixed inset-0 z-50">
              <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileCart(false)} />
              <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl animate-slide-up max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                  <div className="flex items-center space-x-2">
                    <ShoppingCart className="w-5 h-5 text-amber-600" />
                    <h3 className="font-bold text-gray-900">Keranjang</h3>
                    {cart.length > 0 && (
                      <span className="text-xs font-bold text-white bg-amber-500 px-2 py-0.5 rounded-full">{cart.length}</span>
                    )}
                  </div>
                  <button onClick={() => setShowMobileCart(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* Scrollable: Cart Items (compact list) + Discount Details */}
                <div className="flex-1 overflow-y-auto px-4 py-2.5">
                  {cart.map((item, idx) => {
                    const diskonRp = getItemDiskonRp(item);
                    const subtotalDiskon = getItemSubtotalDiskon(item);
                    return (
                      <div key={item.productId} className="border-b border-gray-100 last:border-0">
                        {/* Row 1: compact item row */}
                        <div className="flex items-center gap-2 py-2.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{item.nama}</p>
                            <p className="text-[11px] text-gray-400">{formatRupiah(item.harga)}</p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => updateQty(item.productId, -1)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 active:bg-gray-200 text-gray-500">
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-6 text-center text-sm font-bold text-gray-900">{item.quantity}</span>
                            <button onClick={() => updateQty(item.productId, 1)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-50 active:bg-amber-100 text-amber-600">
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-gray-900">{formatRupiah(subtotalDiskon)}</p>
                            <div className="flex items-center gap-2 justify-end">
                              <button onClick={() => toggleItemDiskon(item.productId)}
                                className={`text-[10px] font-medium ${item.diskonTipe ? 'text-red-500' : 'text-gray-400'}`}>
                                {item.diskonTipe ? 'Diskon' : '+ Diskon'}
                              </button>
                              <button onClick={() => removeFromCart(item.productId)} className="text-[10px] text-red-400">
                                Hapus
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Row 2: expandable discount (only if active) */}
                        {item.diskonTipe && (
                          <div className="flex items-center gap-2 pb-2.5 pl-1">
                            <Percent className="w-3 h-3 text-red-400" />
                            <select value={item.diskonTipe} onChange={e => setItemDiskon(item.productId, item.diskon, e.target.value)}
                              className="text-[10px] border border-gray-200 rounded px-1.5 py-1 bg-white">
                              <option value="PERSEN">%</option>
                              <option value="NOMINAL">Rp</option>
                            </select>
                            <input type="number" value={item.diskon || ''}
                              onChange={e => setItemDiskon(item.productId, Number(e.target.value) || 0, item.diskonTipe)}
                              placeholder="0" min="0"
                              className="w-16 text-[10px] border border-gray-200 rounded px-2 py-1 bg-white text-right" />
                            {diskonRp > 0 && (
                              <span className="text-[10px] text-red-500 font-medium">-{formatRupiah(diskonRp)}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Subtotal & Diskon Total — scroll bersama items */}
                  <hr className="border-gray-200" />
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">Subtotal</span>
                    <span className="text-gray-600">{formatRupiah(totalSebelumDiskon)}</span>
                  </div>
                  {totalDiskonItem > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-red-400">Diskon Item</span>
                      <span className="text-red-500">-{formatRupiah(totalDiskonItem)}</span>
                    </div>
                  )}

                  {/* Total discount toggle */}
                  <div className="border border-dashed border-gray-200 rounded-xl p-2.5">
                    <button onClick={() => setShowDiskonTotal(!showDiskonTotal)}
                      className="flex items-center space-x-1.5 text-xs text-gray-500 w-full">
                      <Tag className={`w-4 h-4 ${showDiskonTotal ? 'text-red-500' : ''}`} />
                      <span>{showDiskonTotal ? 'Tutup Diskon Total' : 'Diskon Total'}</span>
                      {showDiskonTotal && getTotalDiskonRp() > 0 && (
                        <span className="ml-auto text-red-500 font-semibold">-{formatRupiah(getTotalDiskonRp())}</span>
                      )}
                    </button>
                    {showDiskonTotal && (
                      <div className="flex items-center space-x-2 mt-2">
                        <select value={diskonTotalTipe} onChange={e => setDiskonTotalTipe(e.target.value)}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white">
                          <option value="PERSEN">%</option>
                          <option value="NOMINAL">Rp</option>
                        </select>
                        <input type="number" value={diskonTotal}
                          onChange={e => setDiskonTotal(e.target.value)}
                          placeholder="0"
                          className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white text-right" />
                        {getTotalDiskonRp() > 0 && (
                          <span className="text-[10px] text-red-500 font-medium">-{formatRupiah(getTotalDiskonRp())}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Fixed Bottom: Total + Payment + Buttons */}
                <div className="flex-shrink-0 border-t border-gray-100 px-4 py-2.5 space-y-1.5 bg-white">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Total</span>
                    <span className="text-lg font-bold text-gray-900">{formatRupiah(totalBelanja)}</span>
                  </div>

                  {totalDiskonAll > 0 && (
                    <div className="text-[10px] text-green-600 font-medium text-right">
                      Hemat {formatRupiah(totalDiskonAll)}
                    </div>
                  )}

                  {/* Payment row */}
                  <div className="flex items-center space-x-1.5">
                    <select value={metode} onChange={e => setMetode(e.target.value)} 
                      className="flex-1 text-xs border border-gray-200 rounded-xl px-2 py-2.5 bg-white">
                      {METODE.map(m => <option key={m} value={m}>{m === 'QRIS' ? '💳 QRIS' : m}</option>)}
                    </select>
                    <div className="relative flex-[2]">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">Rp</span>
                      <input type="number" value={bayar} onChange={e => setBayar(e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-xl pl-7 pr-2.5 py-2.5 bg-white"
                        placeholder="Jumlah bayar" />
                    </div>
                    <button onClick={() => { setBayar(String(totalBelanja)); setBayarPasFlash(true); setTimeout(() => setBayarPasFlash(false), 600); }}
                      disabled={cart.length === 0}
                      className={`px-2.5 py-2.5 text-xs font-semibold rounded-xl border transition-all active:scale-95 disabled:opacity-40 ${
                        bayarPasFlash ? 'bg-green-200 border-green-400 text-green-800' : 'bg-green-50 text-green-700 border-green-200'
                      }`}>
                      Pas
                    </button>
                  </div>

                  {jumlahBayar >= totalBelanja && totalBelanja > 0 && (
                    <div className="text-right text-xs text-green-600 font-semibold">
                      Kembali: {formatRupiah(kembalian)}
                    </div>
                  )}

                  <div className="flex space-x-1.5">
                    <button
                      onClick={() => setShowMobileCart(false)}
                      className="flex-1 flex items-center justify-center space-x-1.5 py-2.5 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 text-amber-700 text-sm font-semibold active:scale-[0.98] transition-all"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Tambah Menu</span>
                    </button>

                    <button onClick={handleCheckout} disabled={cart.length === 0 || checkoutLoading || jumlahBayar < totalBelanja}
                      className="flex-[2] py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white text-sm font-bold rounded-xl shadow-lg shadow-amber-200 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center space-x-1.5">
                      {checkoutLoading ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /><span>Memproses...</span></>
                      ) : (
                        <><DollarSign className="w-5 h-5" /><span>Bayar</span></>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CafePOS;
